import * as React from 'react';

// Minimal shape of MathLive's custom element we rely on — avoids depending on
// mathlive's types at module scope, which would force a client-only import bundle-wide.
export type MathfieldElement = HTMLElement & {
  value: string;
  smartFence: boolean;
  smartSuperscript: boolean;
};

/**
 * Creates and manages a MathLive `<math-field>` custom element inside `containerRef`.
 * MathLive touches `document` and registers a custom element, so it must only run
 * client-side — hence the dynamic import inside an effect rather than a static import.
 */
export function useMathField({
  value,
  onChange,
  disabled,
  placeholder,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mathfieldRef = React.useRef<MathfieldElement | null>(null);

  React.useEffect(() => {
    if (containerRef.current === null) return;

    let cancelled = false;

    void import('mathlive').then(() => {
      if (cancelled || containerRef.current === null) return;

      const mf = document.createElement('math-field') as MathfieldElement;
      mf.value = value;
      mf.smartFence = true;
      mf.smartSuperscript = true;
      if (className) mf.className = className;
      if (disabled) mf.setAttribute('disabled', 'true');
      if (placeholder) mf.setAttribute('placeholder', placeholder);

      const handleInput = () => onChange(mf.value);
      mf.addEventListener('input', handleInput);

      containerRef.current.appendChild(mf);
      mathfieldRef.current = mf;
    });

    return () => {
      cancelled = true;
      mathfieldRef.current?.remove();
      mathfieldRef.current = null;
    };
    // Only re-create the mathfield when mode/disabled/placeholder/className actually change —
    // `value` is synced separately below so typing doesn't tear the element down.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, placeholder, className]);

  // Keep the mathfield in sync when `value` changes from outside. Direct property
  // assignment does not re-fire the `input` event, so no feedback loop.
  React.useEffect(() => {
    const mf = mathfieldRef.current;
    if (mf && mf.value !== value) {
      mf.value = value;
    }
  }, [value]);

  return { containerRef, mathfieldRef };
}
